import React from "react";
import { Award, Lightbulb, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";
import ProgramPageLayout from "../../components/shared/ProgramPageLayout";

const ExperientialLearning: React.FC = () => {
  return (
    <ProgramPageLayout slug="experiential-learning" theme="teal">
      {(program) => (
        <>
          {/* Program Features from Database */}
          {program.features && program.features.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Program Features</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                {program.features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                    className="flex items-start space-x-4 p-6 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                  >
                    <div className="flex-shrink-0">
                      <CheckCircle className="h-6 w-6 text-teal-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{feature}</h3>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Program Overview</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Our Experiential Learning Program emphasizes learning by doing. Students engage in real-world projects, internships, field trips, and hands-on activities that make learning meaningful and memorable. This approach helps students develop practical skills, critical thinking, and a deeper understanding of their subjects.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Learning Methods</h3>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <Lightbulb className="h-5 w-5 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Project-based learning with real-world applications</span>
              </li>
              <li className="flex items-start">
                <Lightbulb className="h-5 w-5 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Internships and work experience opportunities</span>
              </li>
              <li className="flex items-start">
                <Lightbulb className="h-5 w-5 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Field trips and site visits</span>
              </li>
              <li className="flex items-start">
                <Lightbulb className="h-5 w-5 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Collaborative group projects</span>
              </li>
              <li className="flex items-start">
                <Lightbulb className="h-5 w-5 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Reflection and portfolio development</span>
              </li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Benefits</h3>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <Award className="h-5 w-5 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Enhanced retention and understanding of concepts</span>
              </li>
              <li className="flex items-start">
                <Award className="h-5 w-5 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Development of practical, applicable skills</span>
              </li>
              <li className="flex items-start">
                <Award className="h-5 w-5 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Increased engagement and motivation</span>
              </li>
              <li className="flex items-start">
                <Award className="h-5 w-5 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Better preparation for future careers</span>
              </li>
            </ul>
          </motion.div>
        </>
      )}
    </ProgramPageLayout>
  );
};

export default ExperientialLearning;

